import { useCallback, useMemo, useState } from 'react';
import {
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { usePageTitle } from '../../components/shell/AppShell';
import { RouteDataGrid } from '../../components/data-grid/RouteDataGrid';
import type { QuickFilter, RowAction, SearchField } from '../../components/data-grid/RouteDataGrid';
import { EntityDetailDialog, auditTrailFields } from '../../components/dialogs/EntityDetailDialog';
import type { DetailRow } from '../../components/dialogs/EntityDetailDialog';
import { ConfirmDialog } from '../../components/dialogs/ConfirmDialog';
import { fmtDateTime, fmtOrEmDash } from '../../utils/format';
import type { ExportColumn } from '../../utils/export';
import type { SupplierSummary } from '../../api/types';
import {
  useMyPermissions,
  useSuppliersList,
  useSupplierAction,
} from '../../api/queries';
import { SupplierFormDialog } from './SupplierFormDialog';

export default function SuppliersPage() {
  usePageTitle('Machine Vendors');
  const { queryKey, listFn } = useSuppliersList();
  const perms = useMyPermissions();
  const has = useCallback(
    (code: string) => perms.data?.codes?.includes(code) ?? false,
    [perms.data?.codes],
  );

  const [detailRow, setDetailRow] = useState<SupplierSummary | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState<SupplierSummary | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<{
    supplier: SupplierSummary; action: 'disable' | 'reactivate';
  } | null>(null);
  const supplierAction = useSupplierAction(
    confirmTarget?.supplier.id ?? '00000000-0000-0000-0000-000000000000');

  const columns = useMemo<GridColDef<SupplierSummary>[]>(() => [
    { field: 'displayName', headerName: 'Supplier', flex: 1.4,
      renderCell: (p) => (
        <Stack sx={{ py: 0.5 }}>
          <Typography variant="body2">{p.row.displayName}</Typography>
          <Typography variant="caption" color="text.secondary"
                      sx={{ fontFamily: 'monospace' }}>{p.row.code}</Typography>
        </Stack>
      ) },
    { field: 'primaryContactEmail', headerName: 'Contact', flex: 1.2 },
    { field: 'description', headerName: 'Description', flex: 1.3,
      renderCell: (p) => (
        <Typography variant="body2" color="text.secondary"
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {p.row.description ?? '—'}
        </Typography>
      ) },
    { field: 'isActive', headerName: 'Status', width: 110,
      renderCell: (p) => (
        <Chip size="small" label={p.row.isActive ? 'active' : 'disabled'}
              color={p.row.isActive ? 'success' : 'default'} />
      ) },
    { field: 'createdAt', headerName: 'Created', width: 180,
      valueFormatter: (value) => fmtDateTime(value) },
  ], []);

  const searchFields = useMemo<SearchField[]>(() => [
    { kind: 'text',   key: 'displayName', label: 'Supplier name' },
    { kind: 'text',   key: 'code',        label: 'Code' },
    { kind: 'text',   key: 'primaryContactEmail', label: 'Contact email' },
    { kind: 'select', key: 'isActive',    label: 'Status', options: [
      { value: 'true',  label: 'Active' },
      { value: 'false', label: 'Disabled' },
    ] },
  ], []);

  const quickFilters = useMemo<QuickFilter<SupplierSummary>[]>(() => [
    { label: 'All',       filter: () => true },
    { label: 'Active',    filter: (r) => r.isActive },
    { label: 'Disabled',  filter: (r) => !r.isActive },
  ], []);

  const exportColumns = useMemo<ExportColumn<SupplierSummary>[]>(() => [
    { key: 'code',                 label: 'Code' },
    { key: 'displayName',          label: 'Display Name' },
    { key: 'description',          label: 'Description', format: (v) => fmtOrEmDash(v) },
    { key: 'primaryContactEmail',  label: 'Contact Email' },
    { key: 'isActive',             label: 'Active', format: (v) => (v ? 'Yes' : 'No') },
    { key: 'createdAt',            label: 'Created', format: (v) => fmtDateTime(v) },
    { key: 'updatedAt',            label: 'Updated', format: (v) => fmtDateTime(v) },
  ], []);

  const actions = useMemo<RowAction<SupplierSummary>[]>(() => [
    { label: 'View', onClick: (row) => setDetailRow(row) },
    {
      label: 'Edit',
      visible: () => has('platform.suppliers.edit'),
      onClick: (row) => setEditRow(row),
    },
    {
      label: 'Disable',
      destructive: true,
      visible: (row) => has('platform.suppliers.disable') && row.isActive,
      onClick: (row) => setConfirmTarget({ supplier: row, action: 'disable' }),
    },
    {
      label: 'Reactivate',
      visible: (row) => has('platform.suppliers.disable') && !row.isActive,
      onClick: (row) => setConfirmTarget({ supplier: row, action: 'reactivate' }),
    },
  ], [has]);

  const detailFields = useMemo<DetailRow<SupplierSummary>[]>(() => [
    { label: 'Vendor',       render: (r) => r.displayName },
    { label: 'Code',         render: (r) => <code>{r.code}</code> },
    { label: 'Description',  render: (r) => fmtOrEmDash(r.description) },
    { label: 'Contact',      render: (r) => r.primaryContactEmail },
    { label: 'Status',       render: (r) => (
        <Chip size="small" label={r.isActive ? 'active' : 'disabled'}
              color={r.isActive ? 'success' : 'default'} />
      ) },
    ...auditTrailFields<SupplierSummary>(),
  ], []);

  return (
    <>
      <Stack spacing={2}>
        <Typography variant="h4">Machine Vendors</Typography>

        <RouteDataGrid<SupplierSummary>
          entityName="Machine Vendors"
          queryKey={queryKey}
          listFn={listFn}
          columns={columns}
          searchFields={searchFields}
          quickFilters={quickFilters}
          exportColumns={exportColumns}
          reportTitle="ARMS — Machine Vendors"
          actions={actions}
          primaryAction={
            has('platform.suppliers.create')
              ? { label: '+ Add vendor', onClick: () => setCreateOpen(true) }
              : undefined
          }
          onRowClick={(row) => setDetailRow(row)}
          emptyMessage="No machine vendors yet."
        />

        <EntityDetailDialog<SupplierSummary>
          open={detailRow !== null}
          title={detailRow ? detailRow.displayName : 'Vendor'}
          data={detailRow}
          fields={detailFields}
          onClose={() => setDetailRow(null)}
        />

        <SupplierFormDialog
          open={createOpen}
          mode="create"
          onClose={() => setCreateOpen(false)}
        />

        <SupplierFormDialog
          open={editRow !== null}
          mode="edit"
          supplier={editRow ?? undefined}
          onClose={() => setEditRow(null)}
        />

        <ConfirmDialog
          open={confirmTarget !== null}
          title={confirmTarget?.action === 'disable' ? 'Disable supplier' : 'Reactivate supplier'}
          message={confirmTarget ? (
            confirmTarget.action === 'disable'
              ? <>Disable supplier <strong>{confirmTarget.supplier.displayName}</strong>? Operators won't be able to assign new machines to this supplier; existing machines remain unchanged.</>
              : <>Reactivate supplier <strong>{confirmTarget.supplier.displayName}</strong>? It will appear again in machine ownership pickers.</>
          ) : ''}
          confirmLabel={confirmTarget?.action === 'disable' ? 'Disable' : 'Reactivate'}
          destructive={confirmTarget?.action === 'disable'}
          busy={supplierAction.isPending}
          error={(supplierAction.error as { message?: string } | null)?.message}
          onConfirm={() => {
            if (!confirmTarget) return;
            supplierAction.mutate(confirmTarget.action, {
              onSuccess: () => setConfirmTarget(null),
            });
          }}
          onCancel={() => setConfirmTarget(null)}
        />
      </Stack>
    </>
  );
}
